const TOKEN_STORAGE_KEY = "token"
const REFRESH_TOKEN_STORAGE_KEY = "refreshToken"

// Buffer time before token expiry to trigger refresh (30 seconds)
const TOKEN_EXPIRY_BUFFER_MS = 30 * 1000

type TokenData = {
  token: string
  expiresAt: string
}

type SetAuthTokenParams = {
  accessToken: string
  accessTokenExpiresAt: string
  refreshToken: string
  refreshTokenExpiresAt: string
}

type UpdateAccessTokenParams = {
  accessToken: string
  accessTokenExpiresAt: string
}

/**
 * Parse token data from localStorage
 */
const getTokenData = (key: string): TokenData | null => {
  const data = localStorage.getItem(key)
  if (!data) return null

  try {
    const parsed = JSON.parse(data) as TokenData
    if (!parsed.token || !parsed.expiresAt) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Check if token data is expired
 */
const isTokenExpired = (tokenData: TokenData): boolean => {
  return new Date(tokenData.expiresAt) <= new Date()
}

/**
 * Store authentication tokens
 */
export const setAuthTokens = ({
  accessToken,
  accessTokenExpiresAt,
  refreshToken,
  refreshTokenExpiresAt
}: SetAuthTokenParams): boolean | null => {
  try {
    const tokenData: TokenData = {
      token: accessToken,
      expiresAt: accessTokenExpiresAt
    }
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData))

    const refreshTokenData: TokenData = {
      token: refreshToken,
      expiresAt: refreshTokenExpiresAt
    }
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, JSON.stringify(refreshTokenData))

    return true
  } catch (error) {
    console.error("Failed to store authentication token:", error)
    return null
  }
}

/**
 * Update only the access token (used after refresh)
 */
export const updateAccessToken = ({ accessToken, accessTokenExpiresAt }: UpdateAccessTokenParams): boolean | null => {
  try {
    const tokenData: TokenData = {
      token: accessToken,
      expiresAt: accessTokenExpiresAt
    }
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData))
    return true
  } catch (error) {
    console.error("Failed to update access token:", error)
    return null
  }
}

/**
 * Get the current access token if valid
 */
export const getAccessToken = (): string | undefined => {
  const tokenData = getTokenData(TOKEN_STORAGE_KEY)
  if (!tokenData || isTokenExpired(tokenData)) return undefined
  return tokenData.token
}

/**
 * Get the refresh token if valid
 */
export const getRefreshToken = (): string | undefined => {
  const tokenData = getTokenData(REFRESH_TOKEN_STORAGE_KEY)
  if (!tokenData || isTokenExpired(tokenData)) return undefined
  return tokenData.token
}

/**
 * Check if the access token is expired or expiring soon (within buffer time)
 */
export const isAccessTokenExpiringSoon = (): boolean => {
  const tokenData = getTokenData(TOKEN_STORAGE_KEY)
  if (!tokenData) return true

  const expiryTime = new Date(tokenData.expiresAt).getTime()
  return expiryTime - Date.now() <= TOKEN_EXPIRY_BUFFER_MS
}

/**
 * Check if we have a valid refresh token available
 */
export const hasValidRefreshToken = (): boolean => {
  return !!getRefreshToken()
}

/**
 * Clear all authentication tokens
 */
export const clearAuthTokens = (): boolean | null => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
    return true
  } catch (error) {
    console.error("Failed to delete authentication token:", error)
    return null
  }
}
